// config.ts - إعدادات التطبيق
const getBaseUrl = (): string => {
  // استخدام REACT_APP_API_URL من env
  const apiUrl = process.env.REACT_APP_API_URL;
  if (apiUrl) {
    return apiUrl;
  }
  // fallback للتطوير
  return 'http://localhost:8000';
};

// API_URL النهائي
export const API_URL = getBaseUrl();

// WebSocket URL
export const WS_URL = API_URL.replace(/^http/, 'ws') + '/api/ws/kitchen';

// اسم التطبيق
export const APP_NAME = process.env.REACT_APP_APP_NAME || 'نظام إدارة المتجر';

// ========== نظام إدارة التوكن المتقدم ==========

// مفاتيح التخزين
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const TOKEN_EXPIRY_KEY = 'token_expiry';

// وقت انتهاء التوكن (من env أو افتراضي)
const TOKEN_EXPIRE_MINUTES = parseInt(process.env.REACT_APP_TOKEN_EXPIRE_MINUTES || '1440');
const TOKEN_EXPIRE_MS = TOKEN_EXPIRE_MINUTES * 60 * 1000;

// دالة لحفظ التوكن مع وقت انتهاء الصلاحية
export const setToken = (token: string, expiresIn?: number): void => {
  const expiryTime = Date.now() + (expiresIn ? expiresIn * 1000 : TOKEN_EXPIRE_MS);
  sessionStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  console.log('✅ Token saved, expires:', new Date(expiryTime).toLocaleString());
};

// دالة لحفظ توكن التحديث (Refresh Token)
export const setRefreshToken = (refreshToken: string): void => {
  sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  console.log('✅ Refresh token saved');
};

// دالة للحصول على التوكن
export const getToken = (): string | null => {
  return sessionStorage.getItem(TOKEN_KEY);
};

// دالة للحصول على توكن التحديث
export const getRefreshToken = (): string | null => {
  return sessionStorage.getItem(REFRESH_TOKEN_KEY);
};

// دالة للتحقق من صلاحية التوكن
export const isTokenValid = (): boolean => {
  const token = getToken();
  if (!token) return false;
  
  const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return false;
  
  const expiryTime = parseInt(expiryStr);
  return Date.now() < (expiryTime - 5 * 60 * 1000);
};

// دالة للتحقق مما إذا كان التوكن على وشك الانتهاء
export const isTokenExpiringSoon = (thresholdMinutes: number = 10): boolean => {
  const token = getToken();
  if (!token) return false;
  
  const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return false;
  
  const expiryTime = parseInt(expiryStr);
  return Date.now() > (expiryTime - thresholdMinutes * 60 * 1000);
};

// دالة للحصول على وقت انتهاء التوكن المتبقي (بالدقائق)
export const getTokenRemainingMinutes = (): number => {
  const expiryStr = sessionStorage.getItem(TOKEN_EXPIRY_KEY);
  if (!expiryStr) return 0;
  
  const expiryTime = parseInt(expiryStr);
  const remainingMs = expiryTime - Date.now();
  return Math.max(0, Math.floor(remainingMs / (60 * 1000)));
};

// دالة للحصول على المستخدم
export const getUser = (): any | null => {
  const userStr = sessionStorage.getItem(USER_KEY);
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
  return null;
};

// دالة لحفظ المستخدم
export const setUser = (user: any): void => {
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
};

// دالة موحدة للـ headers
export const getAuthHeaders = (): Record<string, string> => {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// دالة للتحقق من صلاحية التوكن
export const isAuthenticated = (): boolean => {
  return !!getToken() && isTokenValid();
};

// دالة لتسجيل الخروج
export const logout = (): void => {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
  sessionStorage.removeItem('last_login');
  console.log('👋 Logged out, cleared all session data');
  window.location.href = '/login';
};

// دالة لتجديد التوكن
export const refreshToken = async (): Promise<boolean> => {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log('❌ لا يوجد توكن تحديث');
    return false;
  }
  
  try {
    console.log('🔄 محاولة تجديد التوكن...');
    const response = await fetch(`${API_URL}/api/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refresh_token: refreshToken })
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.token) {
        setToken(data.token);
        if (data.refresh_token) {
          setRefreshToken(data.refresh_token);
        }
        console.log('✅ تم تجديد التوكن بنجاح');
        return true;
      }
    }
    
    console.log('❌ فشل تجديد التوكن');
    return false;
  } catch (error) {
    console.error('❌ خطأ في تجديد التوكن:', error);
    return false;
  }
};

// دالة للتحقق من اتصال السيرفر
export const checkServerConnection = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${API_URL}/api/health`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.ok;
  } catch {
    return false;
  }
};

// للـ debugging
if (process.env.NODE_ENV !== 'production') {
  console.log('🌐 API_URL:', API_URL);
  console.log('🏷️ APP_NAME:', APP_NAME);
  console.log('🌍 Environment:', process.env.NODE_ENV);
  
  checkServerConnection().then(isConnected => {
    if (isConnected) {
      console.log('✅ السيرفر شغال على:', API_URL);
    } else {
      console.error('❌ السيرفر مش شغال على:', API_URL);
    }
  });
}